import { useState, useEffect, useCallback, useRef } from "react";
import { useAccount, useSignTypedData } from "wagmi";
import { v4 as uuidv4 } from "uuid";
/**
 * Custom hook to handle verification popup functionality.
 *
 * @param {string} domainName - The name of the domain.
 * @param {string} messageText - The content of the message.
 * @param {string} authentoUrl - (Optional) The URL of the Authento webpage
 * @param {"INDIVIDUAL"|"CORPORATE"} userType - (Optional) The type of user.
 * @returns An object containing functions and state variables related to the verification popup
 */
export const useVerifyPopup = ({ domainName, messageText, authentoUrl = "https://app.authento.io", userType, }) => {
    const [status, setStatus] = useState("unstarted");
    const [timestamp, setTimestamp] = useState("");
    const [nonce, setNonce] = useState("");
    const [levelType, setLevelType] = useState("BASIC");
    const popupRef = useRef();
    const { address } = useAccount();
    const { signTypedData } = useSignTypedData({
        account: address,
        domain: { name: domainName },
        message: {
            content: messageText,
            for: domainName,
            timestamp,
            nonce,
        },
        primaryType: "Message",
        types: {
            Message: [
                { name: "content", type: "string" },
                { name: "for", type: "string" },
                { name: "timestamp", type: "string" },
                { name: "nonce", type: "string" },
            ],
        },
        onMutate: () => {
            popupRef.current = window.open(`${authentoUrl}/verify/pending-signature`, "_blank", "toolbar=no, location=no, status=no, menubar=no, width=640, height=720");
            setStatus("pending");
        },
        onSuccess: (data) => {
            setStatus("success");
            if (popupRef.current) {
                const url = new URL(`${authentoUrl}/verify/${domainName}`);
                url.searchParams.append("address", address);
                url.searchParams.append("ts", timestamp);
                url.searchParams.append("nonce", nonce);
                url.searchParams.append("signature", data);
                url.searchParams.append("levelType", levelType);
                if (userType) {
                    url.searchParams.append("type", userType);
                }
                popupRef.current.location.href = url.toString();
            }
        },
        onError: () => {
            popupRef.current?.close();
            setStatus("error");
        },
    });
    const _verify = useCallback(() => {
        setTimestamp(Date.now().toString());
        setNonce(uuidv4());
    }, []);
    const verifyBasic = useCallback(() => {
        setLevelType("BASIC");
        _verify();
    }, []);
    const verifyPoa = useCallback(() => {
        setLevelType("POA");
        _verify();
    }, []);
    useEffect(() => {
        if (nonce) {
            signTypedData();
        }
    }, [nonce]);
    return { verifyBasic, verifyPoa, status };
};
